import { Icons } from '@/components/icons';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { OrganizationCompany } from '@pigello/construct-core';
import { CompanyConfig } from '@pigello/pigello-matrix';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useInfiniteFilterList } from '../hooks/use-infinite-filter-list';
import type { GlobalFilterItem } from '../types';
import {
  GlobalFilerListItemSkeleton,
  GlobalFilterListItem,
} from './filter-list-item';

type OnToggle = (id: string, displayString: string) => void;

const FilterCompanyItem = ({
  company,
  isSelected,
  onToggle,
}: {
  company: OrganizationCompany;
  isSelected: boolean;
  onToggle: OnToggle;
}) => {
  const { data, isPending } = useQuery(
    api.objects_realestate.query.count.queryOptions({
      filters: {
        ownedBy: {
          noop: company.id,
        },
      },
    })
  );

  const count = data?.statistics.count;
  const hasNoRealEstates = count === 0;

  if (hasNoRealEstates)
    return (
      <Tooltip>
        <TooltipTrigger asChild disabled={hasNoRealEstates}>
          <GlobalFilterListItem
            item={company}
            onToggle={onToggle}
            isSelected={isSelected}
          >
            <p className='mt-1 text-xs font-medium'>
              {CompanyConfig.fields.orgNo.verboseName}: {company.orgNo}
            </p>
            {isPending ? (
              <Skeleton className='mt-1.5 h-3.5 w-32' />
            ) : (
              <div className={'mt-1 text-xs'}>{count} fastighet(er)</div>
            )}
          </GlobalFilterListItem>
        </TooltipTrigger>
        <TooltipContent>
          Det är inte möjligt att filtrera på bolag som saknar fastigheter
        </TooltipContent>
      </Tooltip>
    );

  return (
    <GlobalFilterListItem
      item={company}
      onToggle={onToggle}
      isSelected={isSelected}
    >
      <p className='mt-1 text-xs font-medium'>
        {CompanyConfig.fields.orgNo.verboseName}: {company.orgNo}
      </p>
      {isPending ? (
        <Skeleton className='mt-1.5 h-3.5 w-32' />
      ) : (
        <div
          className={cn('mt-1 text-xs', {
            'text-foreground/80': hasNoRealEstates,
          })}
        >
          {count} fastighet(er)
        </div>
      )}
    </GlobalFilterListItem>
  );
};

export function FilterCompanies({
  searchValue,
  onToggle,
  filteredItems,
}: {
  searchValue: string;
  onToggle: OnToggle;
  filteredItems: GlobalFilterItem[];
}) {
  const {
    items: companies,
    isLoading,
    hasNextPage,
    sentryRef,
  } = useInfiniteFilterList(
    useInfiniteQuery(
      api.organization_company.query.infinite.queryOptions({
        params: {
          pageSize: 25,
          search: searchValue || undefined,
        },
      })
    )
  );

  if (isLoading)
    return Array.from({ length: 10 }).map((_, index) => (
      <GlobalFilerListItemSkeleton key={index}>
        <Skeleton className='h-3 w-40' />
        <Skeleton className='h-3 w-32' />
      </GlobalFilerListItemSkeleton>
    ));

  if (!companies.length) {
    return (
      <div className='flex h-full flex-col'>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Icons.company />
            </EmptyMedia>
            <EmptyTitle>Inga bolag hittades</EmptyTitle>
            <EmptyDescription>
              Vi kunde inte hitta några bolag.{' '}
              {searchValue.length > 0 && 'Försök med en annan sökterm.'}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div>
      {companies.map((company) => {
        const isSelected = filteredItems.find(
          (fi) => fi.type === 'global_companies' && fi.id === company.id
        );

        return (
          <FilterCompanyItem
            company={company}
            isSelected={!!isSelected}
            onToggle={onToggle}
            key={company.id}
          />
        );
      })}

      {hasNextPage && (
        <div ref={sentryRef}>
          <GlobalFilerListItemSkeleton>
            <Skeleton className='h-3 w-40' />
            <Skeleton className='h-3 w-32' />
          </GlobalFilerListItemSkeleton>
        </div>
      )}
    </div>
  );
}
